// src/pages/AdminPage.js
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getPendingProperties, approveProperty, rejectProperty } from "../utils/api";
import Spinner from "../components/Spinner";
import toast from "react-hot-toast";

const fmt = (n) => {
  if (!n) return "—";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n?.toLocaleString("en-IN")}`;
};

export default function AdminPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  // Check if user is admin
  useEffect(() => {
    if (!loading && profile?.role !== "admin") {
      toast.error("Access denied. Admin only.");
      navigate("/dashboard");
    }
  }, [profile, loading, navigate]);

  // Fetch pending properties
  useEffect(() => {
    fetchPendingProperties();
  }, []);

  const fetchPendingProperties = async () => {
    setLoading(true);
    try {
      const data = await getPendingProperties();
      setProperties(data);
    } catch (err) {
      toast.error("Failed to load pending properties");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await approveProperty(id);
      setProperties(prev => prev.filter(p => p.id !== id));
      toast.success("Property approved!");
    } catch (err) {
      toast.error("Failed to approve property");
      console.error(err);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Are you sure you want to reject this property?")) return;
    setProcessing(id);
    try {
      await rejectProperty(id);
      setProperties(prev => prev.filter(p => p.id !== id));
      toast.success("Property rejected");
    } catch (err) {
      toast.error("Failed to reject property");
      console.error(err);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) return <Spinner />;

  if (profile?.role !== "admin") {
    return (
      <div style={{ maxWidth: 600, margin: "50px auto", padding: "0 16px", textAlign: "center" }}>
        <div style={{ background: "white", borderRadius: 16, padding: 40, border: "1px solid #e2ece2" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: "#1a2e1a" }}>Access Denied</p>
          <p style={{ color: "#6b7c6b", fontSize: 14 }}>You don't have admin privileges</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 16px 40px" }}>
      {/* HEADER */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 28, color: "#1a2e1a", marginBottom: 4 }}>
          🛡️ Admin Panel
        </h1>
        <p style={{ color: "#6b7c6b", fontSize: 14 }}>Review and approve property listings</p>
      </div>

      {/* STATS */}
      <div style={{ background: "white", borderRadius: 12, padding: 16, border: "1px solid #e2ece2", marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: "#6b7c6b", marginBottom: 8 }}>PENDING REVIEW</p>
        <p style={{ fontSize: 32, fontWeight: 800, color: "#e65100", fontFamily: "'Sora',sans-serif" }}>{properties.length}</p>
      </div>

      {/* PROPERTIES LIST */}
      {properties.length === 0 ? (
        <div style={{ background: "white", borderRadius: 16, padding: 40, border: "1px solid #e2ece2", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: "#1a2e1a" }}>All caught up!</p>
          <p style={{ color: "#6b7c6b", fontSize: 14 }}>No pending properties to review</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {properties.map(p => {
            const img = p.images?.[0] || "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=300&q=70";
            return (
              <div key={p.id} style={{ background: "white", borderRadius: 12, border: "1px solid #e2ece2", overflow: "hidden" }}>
                {/* PROPERTY HEADER */}
                <div style={{ display: "flex", gap: 16, padding: 16 }}>
                  <img src={img} alt="" style={{ width: 120, height: 100, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontWeight: 700, fontSize: 16, color: "#1a2e1a", marginBottom: 4 }}>{p.title}</h3>
                    <p style={{ fontSize: 18, fontWeight: 800, color: "#1a6b3c", fontFamily: "'Sora',sans-serif", marginBottom: 8 }}>
                      {fmt(p.price)}
                    </p>
                    <p style={{ fontSize: 13, color: "#6b7c6b", marginBottom: 4 }}>
                      📍 {p.location} · {p.type} · {p.area} sq.ft
                    </p>
                    <p style={{ fontSize: 13, color: "#6b7c6b" }}>
                      Posted: {new Date(p.createdAt?.toDate?.() || p.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* DESCRIPTION */}
                {p.description && (
                  <div style={{ padding: "0 16px 12px", borderTop: "1px solid #e2ece2", paddingTop: 12 }}>
                    <p style={{ fontSize: 13, color: "#6b7c6b", lineHeight: 1.5 }}>{p.description}</p>
                  </div>
                )}

                {/* OWNER INFO */}
                <div style={{ padding: "0 16px 12px", background: "#f8faf8", fontSize: 13, color: "#6b7c6b" }}>
                  <p>Owner ID: <code style={{ background: "white", padding: "2px 6px", borderRadius: 4 }}>{p.ownerId}</code></p>
                  <p>Contact: <strong>{p.contact}</strong></p>
                </div>

                {/* ACTIONS */}
                <div style={{ display: "flex", gap: 8, padding: 16, borderTop: "1px solid #e2ece2", background: "white" }}>
                  <button
                    onClick={() => handleApprove(p.id)}
                    disabled={processing === p.id}
                    style={{
                      flex: 1,
                      padding: "11px 16px",
                      background: processing === p.id ? "#a8d5b9" : "#1a6b3c",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: processing === p.id ? "not-allowed" : "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {processing === p.id ? "Processing..." : "✓ Approve"}
                  </button>
                  <button
                    onClick={() => handleReject(p.id)}
                    disabled={processing === p.id}
                    style={{
                      flex: 1,
                      padding: "11px 16px",
                      background: processing === p.id ? "#fcc" : "#c0392b",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: processing === p.id ? "not-allowed" : "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {processing === p.id ? "Processing..." : "✕ Reject"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
